import { motion } from 'framer-motion';
import { TrendingUp, Eye, Clock } from 'lucide-react';
const problems = [{
  icon: TrendingUp,
  title: 'Ad Costs Killing Your Profits?',
  description: "Google Ads now average £170 per click for roofing, plumbing and building work right across South Wales. Our SEO, GEO and AI-tuned websites pull in organic, high-margin leads that cost you nothing per click so you keep more money in your pocket.",
  iconBg: 'bg-rose-500/20',
  iconColor: 'text-rose-400',
  glowColor: 'rose'
}, {
  icon: Eye,
  title: 'Invisible on AI & Google Search?',
  description: "ChatGPT, Siri, Gemini and AI overviews are how customers in South Wales find local trades today. Over time our sites will get you seen whether someone in Swansea, Bridgend, Barry, Neath or the Valleys searches on Google Maps, Google Search or asks an AI.",
  iconBg: 'bg-violet-500/20',
  iconColor: 'text-violet-400',
  glowColor: 'violet'
}, {
  icon: Clock,
  title: 'No Time to Keep Up with Digital Changes?',
  description: "You're busy running your business every day - jobs in Cornelly to emergency call-outs in Barry. We handle everything for you: lightning-fast design, local content, ongoing SEO and AI tuning. Your site stays fresh and, in time, ranks higher on Google Maps and brings in more booked jobs - without you lifting a finger.",
  iconBg: 'bg-sky-500/20',
  iconColor: 'text-sky-400',
  glowColor: 'sky'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1] as const
    }
  }
};
export function ProblemSection() {
  return <section className="py-16 lg:py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 mesh-gradient opacity-50" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            We Fix the Problems Killing Your Growth{' '}
            <span className="text-gradient">Right Here in South Wales</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            ​Most roofers, plumbers, builders and small business owners across South Wales  from Bridgend to Barry, face these exact challenges every day. Here’s how we fix them for people on the tools.                              
          </p>
        </motion.div>
        
        {/* Problem Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {problems.map(problem => {
          const hoverBorderClass = problem.glowColor === 'rose' ? 'hover:border-rose-500/50 hover:shadow-rose-500/20' : problem.glowColor === 'violet' ? 'hover:border-violet-500/50 hover:shadow-violet-500/20' : 'hover:border-sky-500/50 hover:shadow-sky-500/20';
          return <motion.div key={problem.title} variants={cardVariants} className="group">
                <div className={`h-full p-6 md:p-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl transition-all duration-500 ${hoverBorderClass} hover:shadow-2xl relative overflow-hidden`}>
                  {/* Subtle icon glow */}
                  <div className={`absolute top-0 left-0 w-32 h-32 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 ${problem.glowColor === 'rose' ? 'bg-rose-500' : problem.glowColor === 'violet' ? 'bg-violet-500' : 'bg-sky-500'}`} />
                  
                  {/* Icon */}
                  <div className={`relative w-14 h-14 rounded-2xl ${problem.iconBg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <problem.icon className={`w-7 h-7 ${problem.iconColor}`} />
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-display text-xl font-bold mb-4 relative md:min-h-[3.5rem]">
                    {problem.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed relative">
                    {problem.description}
                  </p>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}